/**
 * O que faz: encapsula consultas e mutations do frontend para a agenda de retornos programados.
 * Onde usa: páginas e componentes da feature de retornos programados.
 * Conexões: useClinica, React Query, toast global, helper de fuso e RPCs de criação, transição e sincronização com Kanban.
 * Tabelas: lead_returns, leads e user_profiles via RLS; escrita somente pelas RPCs security definer.
 * Data: 24/08/2026
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClinica } from '@/contexts/ClinicaContext';
import { supabase } from '@/integrations/supabase/client';
import { convertSaoPauloToUTC } from '@/utils/timezoneUtils';
import type {
  CreateLeadReturnInput,
  CreateLeadReturnFromKanbanInput,
  LeadReturn,
  ReturnExecutionMode,
  ReturnExecutorType,
  ReturnStatus,
  TransitionLeadReturnInput,
} from './returnTypes';

type JoinedNameRow =
  | { nome?: string | null; nome_completo?: string | null }
  | Array<{ nome?: string | null; nome_completo?: string | null }>
  | null;

interface LeadReturnSelectRow {
  id: string;
  clinica_id: string;
  lead_id: string;
  responsible_profile_id: string | null;
  scheduled_for: string;
  reason: string;
  context_note: string | null;
  executor_type: ReturnExecutorType;
  execution_mode: ReturnExecutionMode;
  status: ReturnStatus;
  draft_message: string | null;
  updated_at: string;
  lead: JoinedNameRow;
  responsible: JoinedNameRow;
}

interface CreateLeadReturnRpcRow {
  id: string;
}

interface TransitionLeadReturnRpcRow {
  id: string;
}

const ACTIVE_DUE_STATUSES: ReturnStatus[] = ['pending', 'claimed', 'awaiting_approval', 'failed'];
const CALENDAR_DAY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const RETURN_SELECT = `
  id, clinica_id, lead_id, responsible_profile_id, scheduled_for, reason,
  context_note, executor_type, execution_mode, status, draft_message, updated_at,
  lead:leads!inner(nome), responsible:user_profiles(nome_completo)
`;

export const returnQueryKeys = {
  all: ['lead-returns'] as const,
  range: (clinicId: string, start: string, end: string) =>
    ['lead-returns', 'range', clinicId, start, end] as const,
  lead: (clinicId: string, leadId: string) =>
    ['lead-returns', 'lead', clinicId, leadId] as const,
  dueCount: (clinicId: string, day: string) =>
    ['lead-returns', 'due-count', clinicId, day] as const,
};

const readJoinedName = (joinedRow: JoinedNameRow, key: 'nome' | 'nome_completo') => {
  // Motivo: o PostgREST pode devolver relação como objeto ou array dependendo do join; o mapper precisa aceitar ambos sem quebrar a UI.
  if (Array.isArray(joinedRow)) {
    return joinedRow[0]?.[key] ?? null;
  }

  return joinedRow?.[key] ?? null;
};

const mapLeadReturn = (row: LeadReturnSelectRow): LeadReturn => ({
  id: row.id,
  clinica_id: row.clinica_id,
  lead_id: row.lead_id,
  lead_name: readJoinedName(row.lead, 'nome') ?? 'Lead sem nome',
  responsible_profile_id: row.responsible_profile_id,
  responsible_name: readJoinedName(row.responsible, 'nome_completo'),
  scheduled_for: row.scheduled_for,
  reason: row.reason,
  context_note: row.context_note,
  executor_type: row.executor_type,
  execution_mode: row.execution_mode,
  status: row.status,
  draft_message: row.draft_message,
  updated_at: row.updated_at,
});

const normalizeReturnList = (rows: LeadReturnSelectRow[] | null | undefined) => {
  // Motivo: o hook sempre devolve lista estável para simplificar componentes consumidores.
  return (rows ?? []).map(mapLeadReturn);
};

const isValidCalendarDate = (year: number, month: number, day: number) => {
  // Motivo: a agenda não pode aceitar dias impossíveis, senão a query falha tarde e com erro menos claro.
  const referenceDate = new Date(Date.UTC(year, month - 1, day));

  return (
    referenceDate.getUTCFullYear() === year &&
    referenceDate.getUTCMonth() === month - 1 &&
    referenceDate.getUTCDate() === day
  );
};

const parseCalendarDay = (day: string) => {
  // Motivo: validar o formato antes de formar a janela UTC evita `Invalid Date` no contador diário.
  const match = CALENDAR_DAY_REGEX.exec(day.trim());

  if (!match) {
    throw new Error('Informe um dia válido no formato YYYY-MM-DD.');
  }

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const parsedDay = Number(dayText);

  if (!isValidCalendarDate(year, month, parsedDay)) {
    throw new Error('Informe um dia válido para consultar os retornos.');
  }

  return { year, month, day: parsedDay };
};

const assertValidDateTimeRange = (start: string, end: string) => {
  // Motivo: o filtro de período precisa falhar cedo com mensagem clara quando a tela envia datas inválidas ou invertidas.
  if (!start.trim()) {
    throw new Error('Informe a data inicial para carregar os retornos.');
  }

  if (!end.trim()) {
    throw new Error('Informe a data final para carregar os retornos.');
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (Number.isNaN(startDate.getTime())) {
    throw new Error('A data inicial dos retornos é inválida.');
  }

  if (Number.isNaN(endDate.getTime())) {
    throw new Error('A data final dos retornos é inválida.');
  }

  if (startDate.getTime() > endDate.getTime()) {
    throw new Error('A data inicial não pode ser maior que a data final.');
  }
};

const buildUtcRangeForSaoPauloDay = (day: string) => {
  // Motivo: o badge diário precisa contar pelo dia comercial de São Paulo, sem depender do timezone local do navegador.
  const { year, month, day: parsedDay } = parseCalendarDay(day);
  const nextDayReference = new Date(Date.UTC(year, month - 1, parsedDay));
  nextDayReference.setUTCDate(nextDayReference.getUTCDate() + 1);

  const nextDayKey = nextDayReference.toISOString().slice(0, 10);

  return {
    startUtc: convertSaoPauloToUTC(`${day}T00:00`),
    endUtcExclusive: convertSaoPauloToUTC(`${nextDayKey}T00:00`),
  };
};

const normalizeScheduledForInput = (scheduledFor: string) => {
  // Motivo: o formulário envia data+hora em São Paulo sem offset; quando já vier ISO UTC, manter o valor evita dupla conversão.
  const sanitizedScheduledFor = scheduledFor.trim();

  if (!sanitizedScheduledFor) {
    throw new Error('Informe a data e hora do retorno.');
  }

  if (LOCAL_DATE_TIME_REGEX.test(sanitizedScheduledFor)) {
    return convertSaoPauloToUTC(sanitizedScheduledFor);
  }

  const parsedDate = new Date(sanitizedScheduledFor);

  if (Number.isNaN(parsedDate.getTime())) {
    throw new Error('Informe uma data e hora válidas para o retorno.');
  }

  return sanitizedScheduledFor;
};

const buildOperationId = (providedOperationId?: string | null) => {
  // Motivo: retries manuais precisam aceitar operation_id externo, mas a UI comum ainda precisa gerar um identificador idempotente.
  if (providedOperationId && providedOperationId.trim().length > 0) {
    return providedOperationId.trim();
  }

  return crypto.randomUUID();
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
};

export const useReturnsRange = (start: string, end: string) => {
  const { clinicaAtiva } = useClinica();

  return useQuery({
    queryKey: clinicaAtiva?.id
      ? returnQueryKeys.range(clinicaAtiva.id, start, end)
      : [...returnQueryKeys.all, 'range-disabled', start, end] as const,
    enabled: Boolean(clinicaAtiva?.id && start && end),
    queryFn: async (): Promise<LeadReturn[]> => {
      if (!clinicaAtiva?.id) {
        return [];
      }

      assertValidDateTimeRange(start, end);

      // Tabela: lead_returns com joins em leads e user_profiles | Operação: SELECT.
      // Campos: agenda principal + nomes allowlistados do lead e do responsável.
      // Filtros: clinica_id ativo e intervalo scheduled_for informado pela tela.
      // Risco: o nome do lead é dado pessoal; a RLS continua limitando o tenant no servidor.
      const { data, error } = await supabase
        .from('lead_returns')
        .select(RETURN_SELECT)
        .eq('clinica_id', clinicaAtiva.id)
        .gte('scheduled_for', start)
        .lte('scheduled_for', end)
        .order('scheduled_for', { ascending: true })
        .order('id', { ascending: true });

      if (error) {
        throw new Error('Não foi possível carregar os retornos programados.');
      }

      return normalizeReturnList(data as LeadReturnSelectRow[] | null);
    },
    staleTime: 60 * 1000,
  });
};

export const useLeadReturns = (leadId: string | null | undefined, targetClinicaId?: string | null) => {
  const { clinicaAtiva } = useClinica();
  // A ficha administrativa fornece a clínica do lead; o cache e a query devem seguir esse escopo.
  const clinicaId = targetClinicaId === undefined ? clinicaAtiva?.id : targetClinicaId;

  return useQuery({
    queryKey: clinicaId && leadId
      ? returnQueryKeys.lead(clinicaId, leadId)
      : [...returnQueryKeys.all, 'lead-disabled', leadId ?? null] as const,
    enabled: Boolean(clinicaId && leadId),
    queryFn: async (): Promise<LeadReturn[]> => {
      if (!clinicaId || !leadId) {
        return [];
      }

      // Tabela: lead_returns com joins em leads e user_profiles | Operação: SELECT.
      // Campos: os mesmos contratos da agenda para o detalhe do lead não divergir do range.
      // Filtros: clinica_id ativo + lead_id informado pelo componente.
      // Risco: dados seguem sujeitos à RLS e não existe bypass por passar lead_id na tela.
      const { data, error } = await supabase
        .from('lead_returns')
        .select(RETURN_SELECT)
        .eq('clinica_id', clinicaId)
        .eq('lead_id', leadId)
        .order('scheduled_for', { ascending: true })
        .order('id', { ascending: true });

      if (error) {
        throw new Error('Não foi possível carregar os retornos deste lead.');
      }

      return normalizeReturnList(data as LeadReturnSelectRow[] | null);
    },
    staleTime: 60 * 1000,
  });
};

export const useDueReturnCount = (day: string) => {
  const { clinicaAtiva } = useClinica();

  return useQuery({
    queryKey: clinicaAtiva?.id
      ? returnQueryKeys.dueCount(clinicaAtiva.id, day)
      : [...returnQueryKeys.all, 'due-count-disabled', day] as const,
    enabled: Boolean(clinicaAtiva?.id && day),
    queryFn: async (): Promise<number> => {
      if (!clinicaAtiva?.id) {
        return 0;
      }

      const { startUtc, endUtcExclusive } = buildUtcRangeForSaoPauloDay(day);

      // Tabela: lead_returns | Operação: SELECT count.
      // Campos: nenhum payload de linha; apenas total exato para badge da agenda.
      // Filtros: clinica_id ativo, statuses ainda acionáveis e janela do dia em São Paulo convertida para UTC.
      // Risco: o contador não expõe conteúdo do retorno; a RLS continua protegendo o tenant.
      const { count, error } = await supabase
        .from('lead_returns')
        .select('id', { count: 'exact', head: true })
        .eq('clinica_id', clinicaAtiva.id)
        .in('status', ACTIVE_DUE_STATUSES)
        .gte('scheduled_for', startUtc)
        .lt('scheduled_for', endUtcExclusive);

      if (error) {
        throw new Error('Não foi possível contar os retornos do dia.');
      }

      return count ?? 0;
    },
    staleTime: 60 * 1000,
  });
};

export const useCreateLeadReturn = (targetClinicaId?: string | null) => {
  const { clinicaAtiva } = useClinica();
  // A seleção serve à validação local; a RPC continua autorizando o lead no servidor.
  const clinicaId = targetClinicaId === undefined ? clinicaAtiva?.id : targetClinicaId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateLeadReturnInput) => {
      if (!clinicaId) {
        throw new Error('Clínica ativa não encontrada para criar o retorno.');
      }

      // Motivo: a mutation deve rejeitar cedo payload vazio para evitar RPC com mensagens genéricas do banco.
      if (!input.lead_id?.trim()) {
        throw new Error('Selecione um lead válido para programar o retorno.');
      }

      // Motivo: o banco exige texto útil; validar no cliente evita round-trip desnecessário para erros previsíveis.
      if (!input.reason?.trim()) {
        throw new Error('Informe o motivo do retorno.');
      }

      const scheduledForUtc = normalizeScheduledForInput(input.scheduled_for);

      const { data, error } = await supabase.rpc('create_lead_return', {
        p_lead_id: input.lead_id,
        p_scheduled_for: scheduledForUtc,
        p_reason: input.reason.trim(),
        p_context_note: input.context_note.trim() || null,
        p_executor_type: input.executor_type,
        p_execution_mode: input.execution_mode,
        p_responsible_profile_id: input.responsible_profile_id,
      });

      if (error) {
        throw new Error('Não foi possível criar o retorno programado.');
      }

      return data as CreateLeadReturnRpcRow | null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: returnQueryKeys.all });
      toast.success('Retorno programado com sucesso.');
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Não foi possível criar o retorno programado.'));
    },
  });
};

export const useCreateLeadReturnFromKanban = (targetClinicaId?: string | null) => {
  const { clinicaAtiva } = useClinica();
  // Não exigir vínculo do perfil admin com a clínica; a RPC valida a permissão e o tenant do lead.
  const clinicaId = targetClinicaId === undefined ? clinicaAtiva?.id : targetClinicaId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateLeadReturnFromKanbanInput) => {
      if (!clinicaId) {
        throw new Error('Clínica ativa não encontrada para agendar o retorno no Kanban.');
      }

      // Validação de negócio: o card só pode entrar na coluna vinculada quando existe lead, etapa, motivo e horário válidos.
      // Consequência: a RPC não é chamada e o card permanece na coluna atual.
      if (!input.lead_id?.trim() || !input.etapa_kanban_id?.trim() || !input.reason?.trim()) {
        throw new Error('Informe lead, coluna e motivo antes de programar o retorno.');
      }

      const scheduledForUtc = normalizeScheduledForInput(input.scheduled_for);

      // Função: create_lead_return_from_kanban | Operação: INSERT em lead_returns + UPDATE em leads na mesma transação.
      // Campos: agenda, responsável, executor e etapa_kanban_id. Filtros: tenant resolvido por auth.uid() dentro da RPC.
      // Risco: motivo e contexto são dados de atendimento; a função não aceita clinica_id do navegador e falha sem mover o card.
      const { data, error } = await supabase.rpc('create_lead_return_from_kanban' as any, {
        p_lead_id: input.lead_id,
        p_etapa_kanban_id: input.etapa_kanban_id,
        p_scheduled_for: scheduledForUtc,
        p_reason: input.reason.trim(),
        p_context_note: input.context_note.trim() || null,
        p_executor_type: input.executor_type,
        p_execution_mode: input.execution_mode,
        p_responsible_profile_id: input.responsible_profile_id,
      });

      if (error) {
        throw new Error('Não foi possível programar o retorno e mover o lead para a coluna vinculada.');
      }

      return data as CreateLeadReturnRpcRow | null;
    },
    onSuccess: () => {
      // Motivo: a mesma ação altera a agenda e o card; invalidar todas as fontes evita cada tela mostrar um lado desatualizado.
      queryClient.invalidateQueries({ queryKey: returnQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['pipeline-leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['chat-inbox'] });
      toast.success('Retorno agendado e lead movido para a coluna vinculada.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Não foi possível sincronizar o retorno com o Kanban.');
    },
  });
};

export const useTransitionLeadReturn = (targetClinicaId?: string | null) => {
  const { clinicaAtiva } = useClinica();
  // A ficha informa o escopo do retorno, sem alterar a autorização interna da RPC.
  const clinicaId = targetClinicaId === undefined ? clinicaAtiva?.id : targetClinicaId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TransitionLeadReturnInput) => {
      if (!clinicaId) {
        throw new Error('Clínica ativa não encontrada para atualizar o retorno.');
      }

      // Motivo: updated_at é a trava de concorrência otimista; sem ele a UI poderia sobrescrever alterações mais novas.
      if (!input.expected_updated_at?.trim()) {
        throw new Error('Atualize a tela antes de alterar este retorno.');
      }

      const nextScheduledFor = input.scheduled_for
        ? normalizeScheduledForInput(input.scheduled_for)
        : null;

      const { data, error } = await supabase.rpc('transition_lead_return', {
        p_return_id: input.return_id,
        p_target_status: input.target_status,
        p_expected_updated_at: input.expected_updated_at,
        p_scheduled_for: nextScheduledFor,
        p_draft_message: input.draft_message?.trim() || null,
        p_operation_id: buildOperationId(input.operation_id),
      });

      if (error) {
        throw new Error('Não foi possível atualizar o retorno programado.');
      }

      return data as TransitionLeadReturnRpcRow | null;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: returnQueryKeys.all });

      const successMessageByStatus: Record<TransitionLeadReturnInput['target_status'], string> = {
        pending: 'Retorno reagendado com sucesso.',
        completed: 'Retorno concluído com sucesso.',
        cancelled: 'Retorno cancelado com sucesso.',
      };

      toast.success(successMessageByStatus[variables.target_status]);
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Não foi possível atualizar o retorno programado.'));
    },
  });
};
